import { z } from 'zod';
import { CHANNEL_LABEL_MAX_LENGTH } from '../../config.ts';
import { fmtPercent, idSchema } from '../primitives.ts';

export const CHANNEL_KINDS = ['TELEGRAM', 'TRADINGVIEW'] as const;
export type ChannelKind = (typeof CHANNEL_KINDS)[number];

export const channelIdSchema = idSchema;

export const channelLabelSchema = z.string().trim().min(1).max(CHANNEL_LABEL_MAX_LENGTH);

export const DEFAULT_CHANNEL_LABELS: Record<ChannelKind, string> = {
  TELEGRAM: 'Telegram channel',
  TRADINGVIEW: 'TradingView',
};

// mtcute reports channel ids as negative; rejects pasted user/message ids.
export const telegramChatIdSchema = z.number().int().negative();

const floorSchema = z.number().min(0).max(20);

export const alertFloorsSchema = z.object({
  /** Percent of the entry. */
  minStopPercent: floorSchema,
  minRewardRisk: floorSchema,
});

export type AlertFloors = z.infer<typeof alertFloorsSchema>;

export const DEFAULT_ALERT_FLOORS: AlertFloors = { minStopPercent: 0.25, minRewardRisk: 0 };

/** Picked, so a channel row's other columns never ride along into a queued job. */
export const floorsOf = ({ minStopPercent, minRewardRisk }: AlertFloors): AlertFloors => ({
  minStopPercent,
  minRewardRisk,
});

export function belowFloors(
  levels: { entryPrice: number; targetPrice: number; stoplossPrice: number },
  floors: AlertFloors,
): string | null {
  const risk = Math.abs(levels.entryPrice - levels.stoplossPrice);
  const away = risk / levels.entryPrice;
  const under = (measured: string, least: string) =>
    `its ${measured}, and this webhook takes nothing under ${least}`;
  if (away * 100 < floors.minStopPercent) {
    return under(
      `stoploss is ${fmtPercent(away, 2)} from entry`,
      fmtPercent(floors.minStopPercent / 100, 2),
    );
  }
  const rewardRisk = Math.abs(levels.targetPrice - levels.entryPrice) / risk;
  return rewardRisk < floors.minRewardRisk
    ? under(`reward:risk is ${fmtRatio(rewardRisk)}`, fmtRatio(floors.minRewardRisk))
    : null;
}

/** Completes "This webhook …". */
export function floorsSaid({ minStopPercent, minRewardRisk }: AlertFloors): string {
  const refused = [
    ...(minStopPercent > 0 ? [`a stoploss under ${minStopPercent}% from entry`] : []),
    ...(minRewardRisk > 0 ? [`reward:risk under ${fmtRatio(minRewardRisk)}`] : []),
  ];
  return refused.length === 0
    ? 'takes every alert, however tight its stoploss'
    : `refuses alerts with ${refused.join(' or ')}`;
}

export const fmtRatio = (ratio: number): string => `${Number(ratio.toFixed(2))}:1`;
